/**
 * Gb74PersonalReportPdf - 個人レポートの PDF 出力専用レイアウト (A4横2ページ)
 *
 * 画面表示 (Gb74PersonalReport) は縦1カラム・4セクションだが、PDF は旧個人レポートと
 * 同じ A4横2ページの割り付けにする。中身の部品は Gb74ReportBlocks で画面と共用し、
 * ここでは「どのページのどこに置くか」だけを組み替える。
 *
 * ■ 1ページ目
 *   左 : 01 あなたのダイヤモンド (タイプ名 + 中央レーダー + 四隅の軸ボックス)
 *   右上: 「あなたは」文言 (プレースホルダー) + 02 思考スタイル
 *   右下: 02 行動スタイル
 * ■ 2ページ目 (2×2)
 *   左上: 03 役割適性        / 左下: 役割適性の解説文 (プレースホルダー)
 *   右上: 04 気をつけたいこと / 右下: 気をつけたいことの解説文 (プレースホルダー)
 *
 * プレースホルダーの3箇所は文言が確定していないため、領域だけを確保している。
 * 文言が決まったら taglineText / roleNoteText / cautionNoteText に流し込む
 * (props 未指定のときだけ「（文言調整中）」のダミー枠を描画する)。
 *
 * 1ページ = .gb74rp-page 1枚。bulkReportPdf がこの要素単位で画像化して PDF に積む。
 * ページの実寸は PAGE_W/PAGE_H px 固定 (A4横 297:210 と同比)。px を小さくするほど
 * A4 に載せたときの文字が大きくなるので、収まる範囲で控えめな幅にしている。
 */
import React, { useMemo } from 'react';
import { computeGbType, buildGb74Report, type Gb74ReportData } from '../utils/gb74Report';
import type { JudgmentPattern } from '../api/analysisApi';
import {
  Gb74CautionCard,
  Gb74DiamondRadar,
  Gb74ReportHeader,
  Gb74RoleList,
  Gb74StyleItem,
  Gb74TypeHeading,
  ROLE_NOTE,
  CAUTION_NOTE,
  type Gb74StyleEntry,
} from './Gb74ReportBlocks';
import './Gb74PersonalReport.scss';
import { PERSONAL_REPORT_NOTES, ROLE_DESCRIPTIONS } from '../utils/personalReportCopy';
import { buildGb74ReportV2 } from '../utils/gb74ReportV2';
import { pickPersonalTexts } from '../utils/personalTextsV2';
import { Gb74TraitsBlock, Gb74RolesBlockV2, Gb74ShineBlock } from './Gb74ReportBlocksV2';
import './Gb74PersonalReportPdf.scss';

/** PDF 1ページの CSS ピクセル寸法 (A4横 297:210 と同比) */
export const PAGE_W = 1000;
export const PAGE_H = 707;

/** ページ単位で画像化するときの目印 (bulkReportPdf が querySelectorAll する) */
export const PDF_PAGE_SELECTOR = '.gb74rp-page';

interface Gb74PersonalReportPdfProps {
  scores: unknown;
  userName?: string;
  /** 回答日 (例: 2026.07.15) */
  answerDate?: string;
  companyName?: string;
  departmentName?: string;
  /** DB (judgment_patterns) の該当パターン。null=該当なし */
  pattern?: JudgmentPattern | null;
  /** 1ページ目 右上「あなたは」の文言。未指定なら DB desc12 (=type.sub) を使う */
  taglineText?: string;
  /** 2ページ目 左下 役割適性の解説文。未指定なら personalReportCopy のたたき台 */
  roleNoteText?: string;
  /** 2ページ目 右下 気をつけたいことの解説文。未指定なら personalReportCopy のたたき台 */
  cautionNoteText?: string;
}

/** 文言未確定の領域。後で差し替えられるよう、枠だけを確保しておく */
const Placeholder: React.FC<{ text?: string; lines?: number }> = ({ text, lines = 3 }) => {
  if (text) {
    return <div className="gb74rp-ph-text">{text}</div>;
  }
  return (
    <div className="gb74rp-ph" style={{ minHeight: `${lines * 20}px` }}>
      （文言調整中）
    </div>
  );
};

/**
 * 6つの役割の一覧表（2ページ目 左下）。
 *
 * 以前は段落の説明文だったが、6つの役割それぞれが何を指すのかが読み取りにくかったため、
 * 「役割名 × 内容」の2列の表にした（2026-08-23 指示）。文言の出どころは
 * 文言DB PA05（ROLE_DESCRIPTIONS のコメント参照）。
 */
const RoleGuideTable: React.FC = () => (
  <>
    <p className="gb74rp-roletbl-lead">{PERSONAL_REPORT_NOTES.roleLead}</p>
    <table className="gb74rp-roletbl">
      <tbody>
        {ROLE_DESCRIPTIONS.map((r) => (
          <tr key={r.name}>
            <th scope="row">{r.name}</th>
            <td className="_s">{r.summary}</td>
            <td className="_d">{r.detail}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

/** styles から見出しで思考/行動を取り出す (無ければ並び順で代用) */
function pickStyles(styles: Gb74ReportData['styles']): {
  thought?: Gb74StyleEntry;
  action?: Gb74StyleEntry;
} {
  const thought = styles.find((s) => s.title.includes('思考')) ?? styles[0];
  const action = styles.find((s) => s.title.includes('行動')) ?? styles.find((s) => s !== thought);
  return { thought, action };
}

export const Gb74PersonalReportPdf: React.FC<Gb74PersonalReportPdfProps> = ({
  scores,
  userName,
  answerDate,
  companyName,
  departmentName,
  pattern,
  taglineText,
  roleNoteText,
  cautionNoteText,
}) => {
  const report = useMemo(() => buildGb74Report(scores, pattern), [scores, pattern]);

  if (!report) {
    return null; // gb74形式でない場合は呼び出し側でスキップ
  }

  const { axis, roles, shadows, styles, caution } = report;
  const gbType = computeGbType(axis);

  // 新方針(v2)の算出と文言。設問データは v1 と同じものを使う。
  // 仕様: docs/新方針20260821/仕様_GB人財レポートの生成ロジック.html
  const v2 = useMemo(() => buildGb74ReportV2(scores as never), [scores]);
  const v2Texts = useMemo(() => pickPersonalTexts(v2), [v2]);

  // 文言の既定値。呼び出し側が明示指定すればそれを優先する。
  //  - 「あなたは」は DB(judgment_patterns) desc12 が唯一源。buildGb74Report が
  //    type.sub に載せている（DB未取得時は軸から算出した説明にフォールバック）。
  //  - 解説2枠は個人差の無い読み方の説明なので personalReportCopy のたたき台を使う。
  const tagline = taglineText ?? report.type.sub;
  // 既定は表。roleNoteText を明示指定したときだけ、従来の文章に差し替える。
  const roleNote = roleNoteText;
  const cautionNote = cautionNoteText ?? PERSONAL_REPORT_NOTES.cautionNote;
  const { thought, action } = pickStyles(styles);

  // ページの実寸は JS 側を唯一源にする (SCSS では寸法を持たない)
  const pageStyle: React.CSSProperties = { width: `${PAGE_W}px`, minHeight: `${PAGE_H}px` };

  const header = (
    <Gb74ReportHeader
      dateText={answerDate}
      companyName={companyName}
      departmentName={departmentName}
      userName={userName}
    />
  );

  return (
    <div className="gb74r gb74rp">
      {/* ===== 1ページ目 ===== */}
      <div className="gb74rp-page" style={pageStyle}>
        {header}
        <div className="gb74rp-p1">
          {/* 左: 01 あなたのダイヤモンド */}
          <div className="gb74rp-p1-main">
            <Gb74TypeHeading gbType={gbType} />
            <Gb74DiamondRadar axis={axis} />
          </div>

          {/* 右: 「あなたは」+ 02 思考/行動のスタイル */}
          {/* 元デザイン(.c-report-single_result-1_sub)は1枚のカードで、
              思考スタイルと行動スタイルを 1fr 1fr で等分し、境界に区切り線を置く。 */}
          <aside className="gb74rp-p1-side">
            <div className="gb74rp-card gb74rp-sub">
              <div className="gb74rp-lead">あなたは</div>
              <Placeholder text={tagline} lines={4} />
              {/* 思考スタイル/行動スタイルには新方針の文言(PA01/PA02)を1文添える。
                  モックの「相手の様子と先の可能性の両方を見て考えます」に当たる部分。 */}
              <div className="gb74rp-sub-dl">
                <div className="gb74rp-sub-item">
                  {thought && <Gb74StyleItem style={thought} />}
                  {v2Texts.thought && (
                    <div className="gb74v2-style-add">
                      <div className="gb74v2-style-add-lead">{v2Texts.thought.見出し}</div>
                      <div className="gb74v2-style-add-txt">{v2Texts.thought.本文}</div>
                    </div>
                  )}
                </div>
                <div className="gb74rp-sub-item">
                  {action && <Gb74StyleItem style={action} />}
                  {v2Texts.action && (
                    <div className="gb74v2-style-add">
                      <div className="gb74v2-style-add-lead">{v2Texts.action.見出し}</div>
                      <div className="gb74v2-style-add-txt">{v2Texts.action.本文}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ===== 2ページ目 ===== */}
      <div className="gb74rp-page" style={pageStyle}>
        {header}
        <div className="gb74rp-p2">
          {/* 左上: あなたが力を出せる役割（旧 役割適性） */}
          <section className="gb74rp-cell">
            <div className="gb74rp-cell-ttl">あなたが力を出せる役割</div>
            <div className="gb74rp-card _fill">
              <Gb74RolesBlockV2 report={v2} texts={v2Texts} />
            </div>
            <div className="gb74r-note">{ROLE_NOTE}</div>
          </section>

          {/* 右上: あなたの持ち味の出方（旧 気をつけたいこと） */}
          <section className="gb74rp-cell">
            <div className="gb74rp-cell-ttl">あなたの持ち味の出方</div>
            <div className="gb74rp-card _fill">
              <Gb74TraitsBlock report={v2} texts={v2Texts} />
            </div>
            <div className="gb74r-note">
              ※ 4つの力に優劣はありません。組み合わせによって、力が出る場面が変わります。
            </div>
          </section>

          {/* 左下: 6つの役割の一覧 */}
          <section className="gb74rp-cell">
            <div className="gb74rp-cell-ttl">役割適性について</div>
            <div className="gb74rp-card _fill">
              {roleNote ? <Placeholder text={roleNote} lines={6} /> : <RoleGuideTable />}
            </div>
          </section>

          {/* 右下: あなたを輝かせるには？（旧 気をつけたいことについて） */}
          <section className="gb74rp-cell">
            <div className="gb74rp-cell-ttl">あなたを輝かせるには？</div>
            <div className="gb74rp-card _fill">
              <Gb74ShineBlock report={v2} texts={v2Texts} />
            </div>
          </section>
        </div>

        <div className="gb74rp-foot">
          <span>GB人財 ベーシック レポート｜GBbrain</span>
          <span>
            本レポートは持ち味を可視化するものであり、能力の優劣を評価するものではありません。
          </span>
        </div>
      </div>
    </div>
  );
};

export default Gb74PersonalReportPdf;
